import { createRoot } from 'react-dom/client';
import './index.css';
import App from './App.tsx';
import { msalConfig } from "./auth/authConfig";
import { MsalProvider } from "@azure/msal-react";
import { EventType, PublicClientApplication } from '@azure/msal-browser';
import type { AuthenticationResult, EventMessage } from '@azure/msal-browser';
import { AuthProvider } from './context/AuthContext.tsx';
import { ThemeProvider } from '@emotion/react';
import theme from './theme.ts';
import { tokenRefreshService } from './services/tokenRefreshService';

export const msalInstance = new PublicClientApplication(msalConfig);

async function bootstrap() {
    // Initialize MSAL
    await msalInstance.initialize();

    // Set active account if available
    const activeAccount = msalInstance.getActiveAccount();
    if (!activeAccount) {
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
            msalInstance.setActiveAccount(accounts[0]);
        }
    }

    // Initialize token refresh service for proactive token management
    tokenRefreshService.initialize(msalInstance);
    console.info('[Bootstrap] Token refresh service initialized');

    // Add event callbacks for account management
    msalInstance.addEventCallback((event: EventMessage) => {
        // Set active account on successful login
        if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
            const payload = event.payload as AuthenticationResult;
            msalInstance.setActiveAccount(payload.account);
        }

        // Set active account on successful token acquisition
        if (event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS && event.payload) {
            const payload = event.payload as AuthenticationResult;
            msalInstance.setActiveAccount(payload.account);
        }

        // Log authentication failures
        if (event.eventType === EventType.LOGIN_FAILURE || event.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
            console.error("Authentication failure:", event);
        }

        // Handle logout
        if (event.eventType === EventType.LOGOUT_SUCCESS) {
            msalInstance.setActiveAccount(null);
            tokenRefreshService.stop(); // Stop token refresh on logout
        }
    });

    // Handle redirect promise
    try {
        const response = await msalInstance.handleRedirectPromise();
        if (response) {
            msalInstance.setActiveAccount(response.account);
        }
    } catch (error) {
        console.error("Error handling redirect promise:", error);
    }

    // Render the application
    createRoot(document.getElementById('root')!).render(
        <MsalProvider instance={msalInstance}>
            <AuthProvider>
                <ThemeProvider theme={theme}>
                    <App />
                </ThemeProvider>
            </AuthProvider>
        </MsalProvider>
    );
}

bootstrap().catch(error => {
    console.error("Failed to bootstrap application:", error);
});